import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { Icon } from '../icon/icon';

export type BadgeColor =
  | 'red'
  | 'green'
  | 'cyan'
  | 'blue'
  | 'orange'
  | 'yellow'
  | 'gray';

export type BadgeSize = 12 | 16;

// Maps the DS status colours to their primitive tokens. Yellow uses dark ink;
// everything else uses white (local divergence from the spec's all-white
// numerals — white on yellow-500 fails contrast).
const BADGE_BG: Record<BadgeColor, string> = {
  red: 'var(--colorscales-red-500)',
  green: 'var(--colorscales-green-500)',
  cyan: 'var(--colorscales-cyan-500)',
  blue: 'var(--colorscales-blue-500)',
  orange: 'var(--colorscales-orange-500)',
  yellow: 'var(--colorscales-yellow-500)',
  gray: 'var(--core-greyscale-400)',
};

/**
 * Badge — a status dot (design system Badge, figma sizes 12/16). Solid colour
 * fill; optionally a count numeral (Open Sans 600 12/16) or a checkmark.
 */
@Component({
  selector: 'app-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon],
  template: `
    <span
      class="inline-flex items-center justify-center text-caption font-semibold"
      [style.height.px]="size()"
      [style.minWidth.px]="size()"
      [style.borderRadius.px]="size() / 2"
      [style.background]="background()"
      [style.color]="foreground()"
      [style.padding]="hasWideCount() ? '0 5px' : '0'"
    >
      @if (check()) {
        <app-icon name="icon-checkmark" [size]="glyphSize()" />
      } @else if (hasCount()) {
        <span class="tabular-nums">{{ count() }}</span>
      }
    </span>
  `,
})
export class Badge {
  readonly color = input<BadgeColor>('red');
  readonly count = input<number | null>(null);
  readonly check = input(false);
  readonly size = input<BadgeSize>(16);

  protected readonly background = computed(() => BADGE_BG[this.color()]);
  protected readonly foreground = computed(() =>
    this.color() === 'yellow' ? 'var(--papercast-black)' : 'var(--white)',
  );
  // Checkmark glyph scales with the dot: 12px at size 16, 9px at size 12.
  protected readonly glyphSize = computed(() => (this.size() === 16 ? 12 : 9));
  protected readonly hasCount = computed(() => this.count() != null);
  protected readonly hasWideCount = computed(() => {
    const c = this.count();
    return c != null && String(c).length > 1;
  });
}
